import React, { useState, useMemo } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { useLanguage } from "../contexts/LanguageContext";
import { toast } from "sonner";
import { Send, Loader2, Smartphone, Mail, Phone, MapPin } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  "webdesign",
  "development",
  "ecommerce",
  "seo",
  "branding",
  "maintenance",
];

const Contact = () => {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const qrValue = useMemo(() => {
    if (typeof window !== "undefined") {
      return `${window.location.origin}${window.location.pathname}#contact`;
    }
    return "https://defalcowebsolution.com/#contact";
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        ...form,
        language: lang,
      });
      setSent(true);
      toast.success(t.contact.form.success);
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.contact.form.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 sm:py-32 px-5 sm:px-8 bg-white"
    >
      <div className="max-w-7xl mx-auto">
        <div className="relative overflow-hidden rounded-[2rem] sm:rounded-[2.5rem] bg-brand text-white p-8 sm:p-14 lg:p-20 grain">
          {/* Decorative */}
          <div className="absolute -top-32 -right-32 w-96 h-96 bg-white/5 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-white/5 rounded-full blur-3xl pointer-events-none" />

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            {/* Left: heading + form */}
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3 mb-5">
                <span className="w-10 h-px bg-white/70" />
                <span className="text-[11px] tracking-[0.25em] font-bold text-white/80">
                  {t.contact.eyebrow}
                </span>
              </div>
              <h2
                className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight leading-tight mb-4"
                data-testid="contact-title"
              >
                {t.contact.title}
              </h2>
              <p className="text-base sm:text-lg text-white/75 max-w-xl leading-relaxed mb-10">
                {t.contact.subtitle}
              </p>

              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                noValidate
              >
                <div className="sm:col-span-2">
                  <label className="block text-xs tracking-[0.2em] font-semibold text-white/70 mb-2">
                    {t.contact.form.name}
                  </label>
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={update("name")}
                    data-testid="contact-name"
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3.5 text-white placeholder:text-white/40 focus:outline-none focus:border-white focus:ring-2 focus:ring-white/30 transition"
                  />
                </div>

                <div>
                  <label className="block text-xs tracking-[0.2em] font-semibold text-white/70 mb-2">
                    {t.contact.form.email}
                  </label>
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    data-testid="contact-email"
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3.5 text-white placeholder:text-white/40 focus:outline-none focus:border-white focus:ring-2 focus:ring-white/30 transition"
                  />
                </div>

                <div>
                  <label className="block text-xs tracking-[0.2em] font-semibold text-white/70 mb-2">
                    {t.contact.form.phone}
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    data-testid="contact-phone"
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3.5 text-white placeholder:text-white/40 focus:outline-none focus:border-white focus:ring-2 focus:ring-white/30 transition"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-xs tracking-[0.2em] font-semibold text-white/70 mb-2">
                    {t.contact.form.service}
                  </label>
                  <select
                    value={form.service}
                    onChange={update("service")}
                    data-testid="contact-service"
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3.5 text-white focus:outline-none focus:border-white focus:ring-2 focus:ring-white/30 transition appearance-none"
                  >
                    <option value="" className="text-brand-ink">
                      {t.contact.form.servicePlaceholder}
                    </option>
                    {SERVICES.map((s) => (
                      <option key={s} value={s} className="text-brand-ink">
                        {t.services.items[s].title}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-xs tracking-[0.2em] font-semibold text-white/70 mb-2">
                    {t.contact.form.message}
                  </label>
                  <textarea
                    rows={4}
                    required
                    minLength={5}
                    value={form.message}
                    onChange={update("message")}
                    data-testid="contact-message"
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3.5 text-white placeholder:text-white/40 focus:outline-none focus:border-white focus:ring-2 focus:ring-white/30 transition resize-none"
                  />
                </div>

                <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-2">
                  <p className="text-xs text-white/50">
                    {t.contact.form.privacy}
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-brand px-7 py-3.5 text-sm font-semibold shadow-lg hover:-translate-y-0.5 hover:shadow-xl transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed shine-hover"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="animate-spin" size={16} />
                        {t.contact.form.submitting}
                      </>
                    ) : (
                      <>
                        {t.contact.form.submit}
                        <Send size={16} />
                      </>
                    )}
                  </button>
                </div>

                {sent && (
                  <div
                    data-testid="contact-success"
                    className="sm:col-span-2 rounded-xl bg-white/10 border border-white/20 p-4 text-sm text-white"
                  >
                    ✓ {t.contact.form.success}
                  </div>
                )}
              </form>
            </div>

            {/* Right: QR + contact details */}
            <aside
              className="lg:col-span-5"
              data-testid="qr-panel"
            >
              <div className="rounded-3xl bg-white/10 backdrop-blur-md border border-white/20 p-8 sm:p-10">
                <div className="flex items-center gap-3 mb-4">
                  <Smartphone size={18} strokeWidth={1.5} />
                  <span className="text-[11px] tracking-[0.25em] font-bold text-white/80">
                    {t.contact.qr.title}
                  </span>
                </div>
                <p className="text-sm text-white/70 leading-relaxed mb-6">
                  {t.contact.qr.subtitle}
                </p>

                <div className="relative mx-auto w-fit">
                  <div className="absolute -inset-3 bg-white/10 rounded-[2rem] blur-xl" aria-hidden />
                  <div className="relative bg-white rounded-3xl p-5 sm:p-6 shadow-2xl">
                    <QRCodeSVG
                      value={qrValue}
                      size={220}
                      level="H"
                      fgColor="#0F2C36"
                      bgColor="#FFFFFF"
                      data-testid="qr-code"
                    />
                  </div>
                </div>

                <div className="mt-8 space-y-3 text-sm">
                  <a
                    href="mailto:defalcowebsolution@outlook.com"
                    className="flex items-center gap-3 text-white/85 hover:text-white transition-colors"
                    data-testid="contact-email-link"
                  >
                    <Mail size={16} strokeWidth={1.5} />
                    defalcowebsolution@outlook.com
                  </a>
                  <a
                    href="tel:+4917621136701"
                    className="flex items-center gap-3 text-white/85 hover:text-white transition-colors"
                    data-testid="contact-phone-link"
                  >
                    <Phone size={16} strokeWidth={1.5} />
                    +49 176 21136701
                  </a>
                  <div className="flex items-center gap-3 text-white/85">
                    <MapPin size={16} strokeWidth={1.5} />
                    Germania · Europa
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
